import type { SupabaseClient } from '@supabase/supabase-js';
import { validateGeneratedContent } from '@lingoping/core';
import type { CloudDeck, DeckWord, GeneratedCardContent } from '@lingoping/core';
import { wordProblem } from './lib/words';

/**
 * The only door to the data. Every action goes through the `admin-decks` Edge Function, which
 * checks `is_admin()` itself (see Docs/plans/CLOUD_DECKS_PLAN.md, "admin-decks Edge Function").
 */

/** A public deck as listed: the deck plus how many of its words already have a card. */
export type AdminDeck = CloudDeck & { ready_count: number };

/** A deck word as the editor sees it: `ready` = its card content exists in the library. */
export type AdminDeckWord = DeckWord & { ready: boolean };

/** A prepared card as the library holds it, plus where its picture stands. */
export type AdminCard = GeneratedCardContent & { image_status: string | null };

/** One row of the Cards screen: a card in the shared library, in short. */
export interface LibraryCardRow {
  id: string;
  lemma: string;
  part_of_speech: string;
  translation: string;
  article: string | null;
  gender: string | null;
  image_url: string | null;
  image_status: string | null;
  /** 'ai' (written by the AI) or 'curated' (written by hand for a seeded deck). */
  content_source: string | null;
  created_at: string;
  /** How many of the 8 extra languages the card has a meaning in. */
  translation_count: number;
  /** Accounts that are learning this word now. */
  learners: number;
  /** 'Admin', the email of the learner who first added the word, or null when not recorded. */
  created_by: string | null;
}

export interface LibraryQuery {
  search?: string;
  partOfSpeech?: string | null;
  /** 1-based. */
  page?: number;
  pageSize?: number;
}

export interface WordInput {
  lemma: string;
  context?: string;
}

export interface DeckFields {
  title: string;
  description?: string;
  group_name?: string | null;
  sort_order?: number;
}

export interface PrepareFailure {
  lemma: string;
  error: string;
}

export interface PrepareResult {
  prepared: number;
  failed: PrepareFailure[];
  remaining: number;
}

export interface SetWordsResult {
  deck: CloudDeck;
  added: number;
  removed: number;
  kept: number;
}

export type AdminErrorCode = 'not_admin' | 'not_ready' | 'unauthenticated' | 'network' | 'unknown';

export class AdminApiError extends Error {
  readonly code: AdminErrorCode;
  /** The server's own error code when it sent one we do not map (for example `deck_not_found`). */
  readonly serverCode?: string;
  readonly status?: number;

  constructor(
    code: AdminErrorCode,
    message: string,
    opts?: { serverCode?: string; status?: number }
  ) {
    super(message);
    this.name = 'AdminApiError';
    this.code = code;
    this.serverCode = opts?.serverCode;
    this.status = opts?.status;
  }
}

const MESSAGES: Record<AdminErrorCode, string> = {
  not_admin: 'This account has no admin access.',
  not_ready: 'Every word needs a prepared card before the deck can be published.',
  unauthenticated: 'Your session has ended. Sign in again.',
  network: 'Could not reach the server. Check your connection and try again.',
  unknown: 'Something went wrong. Try again.',
};

export function errorMessage(err: unknown): string {
  if (err instanceof AdminApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return MESSAGES.unknown;
}

/** The subset of the Supabase client the API needs (lets tests pass a fake). */
export type FunctionsClient = Pick<SupabaseClient, 'functions'>;

interface ErrorBody {
  error?: unknown;
  code?: unknown;
  message?: unknown;
  /** `invalid_word`: position of the rejected word in the list (0-based) and the word itself. */
  index?: unknown;
  lemma?: unknown;
  /** `too_many_words`: the limit. `not_ready`: words still without a card. */
  max?: unknown;
  remaining?: unknown;
}

/** Plain-language text for the server's own error codes (anything else shows the code). */
function serverCodeMessage(code: string, body: ErrorBody): string | undefined {
  switch (code) {
    case 'invalid_word': {
      const lemma = typeof body.lemma === 'string' ? body.lemma : '';
      const where = typeof body.index === 'number' ? ` (word ${body.index + 1} in the list)` : '';
      const why = wordProblem(lemma) ?? 'It is not a word or short phrase the server accepts.';
      return `“${lemma || '(empty)'}”${where} cannot be saved. ${why} Remove or fix it, then save again.`;
    }
    case 'too_many_words':
      return `A deck can hold at most ${typeof body.max === 'number' ? body.max.toLocaleString('en') : 'a few thousand'} words.`;
    case 'deck_too_large':
      return 'This deck has reached the maximum number of words.';
    case 'empty_deck':
      return 'Add at least one word before publishing.';
    case 'word_not_found':
      return 'This card was not found. If you just changed the deck, save it and try again.';
    case 'deck_not_found':
      return 'This deck no longer exists. Go back to the list and reload.';
    case 'invalid_title':
      return 'The name must be between 1 and 120 characters.';
    case 'invalid_description':
      return 'The description can be at most 500 characters.';
    case 'invalid_group_name':
      return 'The group can be at most 80 characters.';
    case 'invalid_sort_order':
      return 'The order must be a whole number.';
    case 'user_not_found':
      return 'This account no longer exists.';
    case 'invalid_days':
      return 'Days must be a whole number from 1 to 365.';
    case 'invalid_reason':
      return 'Add a short reason (up to 500 characters).';
    case 'invalid_ends_at':
      return 'The end date must be in the future.';
    case 'no_grant':
      return 'This account has no free access to take back.';
    case 'rule_exists':
      return 'There is already a rule for this email, moment and day.';
    case 'template_not_found':
      return 'This email template no longer exists.';
    case 'rule_not_found':
      return 'This rule no longer exists. Reload the page.';
    case 'invalid_from':
    case 'invalid_to':
    case 'invalid_range':
      return 'Pick a valid date range.';
    case 'range_too_long':
      return 'Pick a range of at most five years.';
    case 'server_not_configured':
      return 'The server is not set up correctly. Nothing was changed.';
    default:
      return undefined;
  }
}

function codeFromBody(body: ErrorBody | null | undefined): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const e = body.error;
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') {
    const inner = e as ErrorBody;
    if (typeof inner.code === 'string') return inner.code;
    if (typeof inner.message === 'string') return inner.message;
  }
  if (typeof body.code === 'string') return body.code;
  return undefined;
}

function messageFromBody(body: ErrorBody | null | undefined): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  if (typeof body.message === 'string') return body.message;
  const e = body.error;
  if (e && typeof e === 'object' && typeof (e as ErrorBody).message === 'string') {
    return (e as ErrorBody).message as string;
  }
  return undefined;
}

function mapFailure(status: number | undefined, body: ErrorBody | null | undefined): AdminApiError {
  const serverCode = codeFromBody(body);
  const serverMessage = messageFromBody(body);
  if (serverCode === 'not_admin' || status === 403) {
    return new AdminApiError('not_admin', MESSAGES.not_admin, { serverCode, status });
  }
  if (serverCode === 'not_ready') {
    return new AdminApiError('not_ready', MESSAGES.not_ready, { serverCode, status });
  }
  if (status === 401 || serverCode === 'unauthenticated' || serverCode === 'unauthorized') {
    return new AdminApiError('unauthenticated', MESSAGES.unauthenticated, { serverCode, status });
  }
  const known = serverCode && body ? serverCodeMessage(serverCode, body) : undefined;
  return new AdminApiError('unknown', known || serverMessage || serverCode || MESSAGES.unknown, {
    serverCode,
    status,
  });
}

/** Turns whatever `functions.invoke` reports into an AdminApiError. */
async function mapInvokeError(error: unknown): Promise<AdminApiError> {
  const err = error as { name?: string; context?: unknown; message?: string } | null;
  const name = err?.name ?? '';
  if (name === 'FunctionsFetchError') {
    return new AdminApiError('network', MESSAGES.network);
  }
  if (name === 'FunctionsRelayError') {
    return new AdminApiError('unknown', err?.message || MESSAGES.unknown);
  }
  // FunctionsHttpError: `context` is the fetch Response.
  const res = err?.context as Response | undefined;
  if (res && typeof res.json === 'function') {
    const body = await res
      .clone()
      .json()
      .then(
        (b) => b as ErrorBody,
        () => null
      );
    return mapFailure(res.status, body);
  }
  if (err instanceof TypeError || /failed to fetch|network/i.test(err?.message ?? '')) {
    return new AdminApiError('network', MESSAGES.network);
  }
  return new AdminApiError('unknown', err?.message || MESSAGES.unknown);
}

export interface AdminApi {
  listDecks(): Promise<AdminDeck[]>;
  getDeck(deckId: string): Promise<{ deck: CloudDeck; words: AdminDeckWord[] }>;
  /** The prepared card of one deck word (`lemma` as the deck writes it). */
  getCard(deckId: string, lemma: string): Promise<AdminCard>;
  /** One page of every card in the shared library, newest first. */
  listCards(query?: LibraryQuery): Promise<{ cards: LibraryCardRow[]; total: number }>;
  /** A card of the shared library by its id. */
  getLibraryCard(wordId: string): Promise<AdminCard>;
  createDeck(fields: DeckFields & { language?: string }): Promise<CloudDeck>;
  updateDeck(deckId: string, fields: Partial<DeckFields>): Promise<CloudDeck>;
  setWords(deckId: string, words: WordInput[]): Promise<SetWordsResult>;
  prepare(deckId: string, limit?: number): Promise<PrepareResult>;
  publish(deckId: string): Promise<CloudDeck>;
  unpublish(deckId: string): Promise<CloudDeck>;
  deleteDeck(deckId: string): Promise<void>;
}

export const MAX_WORDS_PER_DECK = 5000;
export const DEFAULT_PREPARE_LIMIT = 20;
export const MAX_PREPARE_LIMIT = 50;

type CardAnswer = { card?: { image_status?: unknown } };

/** Checks a card the server sent with the same rules the extension applies before showing one. */
function toAdminCard(answer: CardAnswer): AdminCard {
  const content = validateGeneratedContent(answer.card);
  if (!content) {
    throw new AdminApiError(
      'unknown',
      'This card is stored in a form the app cannot show. Prepare it again.'
    );
  }
  const status = answer.card?.image_status;
  return { ...content, image_status: typeof status === 'string' ? status : null };
}

/** Calls one action of an admin Edge Function and turns every failure into an AdminApiError. */
export function makeCall(client: FunctionsClient, functionName: string) {
  return async function call<T>(action: string, body: Record<string, unknown> = {}): Promise<T> {
    let result: { data: unknown; error: unknown };
    try {
      result = await client.functions.invoke(functionName, { body: { action, ...body } });
    } catch (e) {
      throw await mapInvokeError(e);
    }
    if (result.error) throw await mapInvokeError(result.error);
    const data = result.data as (T & ErrorBody) | null;
    if (!data || typeof data !== 'object') {
      throw new AdminApiError('unknown', MESSAGES.unknown);
    }
    // A 200 that still carries an error code.
    if (typeof data.error === 'string' || (data.error && typeof data.error === 'object')) {
      throw mapFailure(undefined, data);
    }
    return data;
  };
}

export function createAdminApi(client: FunctionsClient): AdminApi {
  const call = makeCall(client, 'admin-decks');

  return {
    async listDecks() {
      const r = await call<{ decks: AdminDeck[] }>('list');
      return r.decks ?? [];
    },
    async getDeck(deckId) {
      const r = await call<{ deck: CloudDeck; words: AdminDeckWord[] }>('get', { deck_id: deckId });
      return { deck: r.deck, words: r.words ?? [] };
    },
    async getCard(deckId, lemma) {
      return toAdminCard(await call<CardAnswer>('card', { deck_id: deckId, lemma }));
    },
    async listCards(query = {}) {
      const r = await call<{ cards?: LibraryCardRow[]; total?: number }>('cards', {
        search: query.search ?? '',
        ...(query.partOfSpeech ? { part_of_speech: query.partOfSpeech } : {}),
        page: query.page ?? 1,
        page_size: query.pageSize ?? 50,
      });
      return { cards: r.cards ?? [], total: r.total ?? 0 };
    },
    async getLibraryCard(wordId) {
      return toAdminCard(await call<CardAnswer>('library_card', { word_id: wordId }));
    },
    async createDeck(fields) {
      const r = await call<{ deck: CloudDeck }>('create', { ...fields });
      return r.deck;
    },
    async updateDeck(deckId, fields) {
      const r = await call<{ deck: CloudDeck }>('update', { deck_id: deckId, ...fields });
      return r.deck;
    },
    async setWords(deckId, words) {
      return call<SetWordsResult>('set_words', {
        deck_id: deckId,
        words: words.map((w) =>
          w.context ? { lemma: w.lemma, context: w.context } : { lemma: w.lemma }
        ),
      });
    },
    async prepare(deckId, limit = DEFAULT_PREPARE_LIMIT) {
      const r = await call<PrepareResult>('prepare', {
        deck_id: deckId,
        limit: Math.min(Math.max(1, Math.floor(limit)), MAX_PREPARE_LIMIT),
      });
      return { prepared: r.prepared ?? 0, failed: r.failed ?? [], remaining: r.remaining ?? 0 };
    },
    async publish(deckId) {
      const r = await call<{ deck: CloudDeck }>('publish', { deck_id: deckId });
      return r.deck;
    },
    async unpublish(deckId) {
      const r = await call<{ deck: CloudDeck }>('unpublish', { deck_id: deckId });
      return r.deck;
    },
    async deleteDeck(deckId) {
      await call<{ ok: true }>('delete', { deck_id: deckId });
    },
  };
}
